/**
 * @name MenuApps
 * @description Menú de aplicaciones de Talentry
 * @author Ricardo Guerra
 */

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import { SessionService, AuthService } from '@services';
import { Environment } from '@config';
import { Icons, MUI, Typography } from '@styles';

const MenuApps = () => {
  const [apps, setApps] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const setAppsHandler = useCallback(() => {
    const isAdmin = SessionService.hasRoles([3]);
    const userInfo = JSON.parse(localStorage.getItem('ls.infoUsuario')) || {};
    const userApps = Array.isArray(userInfo.Aplicacion) ? [...userInfo.Aplicacion] : [];

    // NOTA: 
    // Si el usuario tiene Indicadores Acumulados pero no Indicadores, entonces se añade la información de la app (icono y nombre) a mostrar.
    // Si el usuario tiene ambas apps (Indicadores e Indicadores Acumulados) se elimina de la lista Indicadores Acumulados ya que es la que se debe mostrar.

    const hasInd = userApps.map((app) => app.IdAplicacion).indexOf(3) > -1;
    const hasIndAcc = userApps.map((app) => app.IdAplicacion).indexOf(8) > -1;
    const indexIndAcc = userApps.findIndex((app) => app.IdAplicacion === 8);

    if (hasIndAcc && !hasInd) {
      userApps[indexIndAcc].Aplicacion = 'Indicadores'; // TODO: Etiqueta de diccionaro
      userApps[indexIndAcc].Logo = 'https://assets.talentry.io/icons/appicons/metrics.png';
    } else if (hasIndAcc && hasInd) {
      userApps.splice(indexIndAcc, 1);
    }

    // Si el usuario no es Admin se elimina Check In de la lista.
    const hasCheckIn = userApps.map((app) => app.IdAplicacion).indexOf(16) > -1;
    if (!isAdmin && hasCheckIn) {
      const indexCheckIn = userApps.findIndex((app) => app.IdAplicacion === 16);
      userApps.splice(indexCheckIn, 1);
    }

    setApps(userApps);
  }, []);

  useEffect(() => {
    setAppsHandler();
  }, [setAppsHandler]);

  const openHandler = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const closeHandler = useCallback(() => {
    setAnchorEl(null);
  }, []);


  // Redireccionar a la aplicación
  const goToApp = useCallback((app) => {
    // console.log('goToApp', app);
    let URL = Environment.getCurrent().domain;
    // console.log('URL', URL);
    switch (app.IdAplicacion) {
      case 1: // Configuración
        URL += 'configuracion/dashboard';
        break;

      case 2: // Administración
        URL += 'administracion/dashboard';
        // PreviousRouteService.setPreviousRoute(`talentry/#/administracion/dashboard`);
        break;

      case 3: // Indicadores
        URL = `${URL.substring(0, URL.length - 2)}indicators`;
        break;

      case 4: // Reconocimientos
        URL = `${URL.substring(0, URL.length - 2)}acknowledgments`;
        break;

      case 5: // Mis datos
        URL += 'datos/misDatos';
        break;

      case 6: // Evaluaciones
        URL += 'evaluaciones/dashboard';
        break;

      case 8: // Indicadores Acumulados
        URL = `${URL.substring(0, URL.length - 2)}indicators/accum-metrics`;
        break;

      case 9: // Plan de Desarrollo
        URL = `${URL.substring(0, URL.length - 2)}development-plan`;
        break;

      case 10: // Academy
        URL = `${URL.substring(0, URL.length - 2)}academy`;
        break;

      case 11: // Organigrama
        URL += 'organigrama/chart';
        break;

      case 12: // Auditoría
        URL = `${URL.substring(0, URL.length - 2)}audit`;
        break;

      case 13: // Comunicación
        URL = `${URL.substring(0, URL.length - 2)}communication`;
        // PreviousRouteService.setPreviousRoute(`communication/#/`);
        break;

      case 14: // Sueldos y Promociones
        URL = `${URL.substring(0, URL.length - 2)}salary-promotions`;
        break;

      case 15: // Dashboards
        URL = `${URL.substring(0, URL.length - 2)}dashboards`;
        break;

      case 16: // Check In
        URL = `${URL.substring(0, URL.length - 2)}check-in`;
        break;

      case 17: // Help Desk
        AuthService.clearStorage();
        AuthService.logout();
        break;

      case 18: // Reclutamiento
        URL = `${URL.substring(0, URL.length - 2)}recruitment`;
        break;

      case 19: // Expediente 360
        URL = `${URL.substring(0, URL.length - 2)}org-chart-360`;
        break;

      case 20: // Buzón de denuncias
        URL = `${URL.substring(0, URL.length - 2)}mailbox`;
        break;

      case 21: // Centro de Ayuda
        URL = `${URL.substring(0, URL.length - 2)}help-center`;
        break;

      default:
        break;
    }

    window.location.href = URL;
  }, []);

  // Redireccionar al Home
  const goHome = useCallback(() => {
    let URL = Environment.getCurrent().domain;
    window.location.href = `${URL.substring(0, URL.length - 2)}talentry/#/home`;
  }, []);

  return (
    <>
      <Styled.BoxTopbarIcon onClick={openHandler}>
        <MUI.Tooltip title={i18n.t('GenericMessages.apps')}>
          <Icons.Apps />
        </MUI.Tooltip>
      </Styled.BoxTopbarIcon>

      <Styled.Popover
        open={Boolean(anchorEl)}
        onClose={closeHandler}
        anchorEl={anchorEl}
      >
        <MUI.MenuHeader>
          <Styled.BoxHome
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            width="100%"
            onClick={goHome}
          >
            <Icons.Home />
            <Typography.Text>{i18n.t('Topbar.home')}</Typography.Text>
          </Styled.BoxHome>
        </MUI.MenuHeader>

        {/* <MenuItem> */}
        <Styled.BoxApps>
          {apps.map((app, i) => {
            if (app.IdAplicacion !== 8) { // Indicadores Acumulados
              return (
                <Styled.BoxRow
                  key={i}
                  apps={apps.length}
                >
                  <Styled.BoxApp onClick={() => goToApp(app)}>
                    <Styled.BoxImage>
                      <Styled.AppImage
                        src={app.Logo}
                        alt={app.Aplicacion}
                        appId={app.IdAplicacion}
                      />
                    </Styled.BoxImage>
                    <Styled.AppText>
                      {app.Aplicacion}
                    </Styled.AppText>
                  </Styled.BoxApp>
                </Styled.BoxRow>
              );
            }
            return null;
          })}
          {apps.length === 0 && (
            <Styled.BoxNoApps>
              <Typography.Text>{i18n.t('Topbar.noApps')}</Typography.Text>
            </Styled.BoxNoApps>
          )}
        </Styled.BoxApps>
        {/* </MenuItem> */}
      </Styled.Popover>
    </>
  );
};

MenuApps.propTypes = {
  // History router.
  history: PropTypes.object
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxTopbarIcon = styled(MUI.ButtonText)`
  &.MuiButton-root{
    display: flex;
    min-width: 60px;
    width: 60px;
    height: 60px;
    padding: 12px;
    margin: 0;

    &:hover {
      text-decoration: none;
      background-color: ${(props) => props.theme.colors.greyHover};
      border-radius: 0%;
    }
  }
`;

Styled.BoxImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
`;

Styled.AppImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin: auto;

  width: ${(props) => {
    if (props.appId === 6) { return '35px'; }  // Evaluaciones
    if (props.appId === 9) { return '42.5px'; }  // Plan de Desarrollo
    if (props.appId === 11) { return '42.5px'; }  // Organigrama
    if (props.appId === 12) { return '37.5px'; }  // Auditoría
    if (props.appId === 18) { return '42.5px'; }  // Reclutamiento
    if (props.appId === 19) { return '37.5px'; }  // Expediente
    if (props.appId === 20) { return '42.5px'; }  // Buzón
    if (props.appId === 21) { return '35px'; }  // Centro de Ayuda
  }};

  height: ${(props) => {
    if (props.appId === 6) { return '35px'; }  // Evaluaciones
    if (props.appId === 9) { return '42.5px'; }  // Plan de Desarrollo
    if (props.appId === 11) { return '42.5px'; }  // Organigrama
    if (props.appId === 12) { return '37.5px'; }  // Auditoría
    if (props.appId === 18) { return '42.5px'; }  // Reclutamiento
    if (props.appId === 19) { return '37.5px'; }  // Expediente
    if (props.appId === 20) { return '42.5px'; }  // Buzón
    if (props.appId === 21) { return '35px'; }  // Centro de Ayuda
  }};
`;

Styled.AppText = styled(Typography.Text)`
  font-family: 'Bryant Pro Medium';
  font-size:  ${(props) => props.theme.typography.smallText};
  margin: 12px auto;
`;

Styled.BoxHome = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
  .MuiSvgIcon-root {
    margin-right: 12px;
    font-size: 20px;
  }
`;

Styled.BoxApps = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  min-width: 180px;
  max-width: 548px;
  min-height: 110px;
  max-height: 470px;    
  overflow-y: auto;
  overflow-x: hidden;
  background: ${(props) => props.theme.colors.white};
  scrollbar-width: thin !important; /* Scrollbar Firefox */
`;

Styled.BoxRow = styled.div`
  display: flex;
  width: ${(props) => {
    if (props.apps === 1) {
      return '100%';
    } else if (props.apps === 2) {
      return '50%';
    } else {
      return '33.3%';
    }
  }};
`;

Styled.BoxApp = styled(MUI.ButtonText)`
  &.MuiButton-root {
    width: 100%;
    height: 110px;
    min-width: 180px;
    padding: 12px;
    margin: 0;
    border-radius: 0;
    
    &:hover {
      text-decoration: none;
      background: ${(props) => props.theme.colors.greyHover};
    }
  }

  .MuiButton-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
`;

Styled.BoxNoApps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

// *************************************
// GLOBAL STYLED COMPONENTS             
// *************************************

Styled.Popover = styled(MUI.Popover)`
  .MuiPopover-paper {
    max-height: 520px;
    max-width: 548px;
    left: auto !important;
    right: 16px !important;
    top: 60px !important;
    overflow-y: hidden;
    overflow-x: hidden;
  }
`;

export default MenuApps;
